/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type ReactElement } from 'react';
import { cn } from '@/lib/utils';
import { useTrueVaultPrivacyLinks } from './use-truevault-privacy-links';

/**
 * RGBARRY-CUSTOMIZATION (DRAFT, W-23789028 consent rework): the visible TrueVault "Website
 * Implementation" compliance links (per TrueVault's Developer Instructions).
 *
 * These are the shopper-facing links Polaris expects on the site: the California Privacy Notice (at
 * every point of collection — email/SMS signup, account creation, checkout, forms), the Notice of
 * Financial Incentive (at opt-in points that offer a discount/incentive), and the "Your Privacy
 * Choices" opt-out (footer). Each carries the TrueVault-mandated CSS class Polaris keys on
 * (`truevault-polaris-privacy-notice` / `truevault-polaris-optout`) and is rendered `hidden`: Polaris
 * REVEALS the ones relevant to the visitor's state at runtime and keeps the rest hidden. Keep the
 * class and the exact link text — do not translate or rename them (compliance requirement).
 *
 * Gating: every component renders `null` until BOTH the CMP loader (`scriptUrl`) AND the Privacy
 * Center base (`privacyCenterUrl`) are validly configured (see getTrueVaultPrivacyLinks). That is the
 * same single switch that loads Polaris (which is what un-hides these links) — so callers fall back to
 * the pre-TrueVault internal links until TrueVault is turned on, leaving no compliance gap in between.
 * The config hook these components consume lives in ./use-truevault-privacy-links (kept in its own
 * module so this file stays component-only for the fast-refresh lint rule).
 */

/**
 * California Privacy Notice link — place at points of collection (signup, account creation, checkout,
 * any form). Rendered `hidden`; Polaris reveals it for visitors from states where it applies.
 */
export function CaliforniaPrivacyNoticeLink({ className }: { className?: string }): ReactElement | null {
    const links = useTrueVaultPrivacyLinks();
    if (!links) return null;
    return (
        <a
            className={cn('truevault-polaris-privacy-notice', className)}
            href={links.californiaPrivacyNotice}
            rel="noopener noreferrer"
            hidden>
            California Privacy Notice
        </a>
    );
}

/**
 * Notice of Financial Incentive link — place at opt-in points that offer a financial incentive (e.g.
 * "sign up for a discount"). Uses the same `truevault-polaris-privacy-notice` class Polaris reveals.
 */
export function FinancialIncentiveNoticeLink({ className }: { className?: string }): ReactElement | null {
    const links = useTrueVaultPrivacyLinks();
    if (!links) return null;
    return (
        <a
            className={cn('truevault-polaris-privacy-notice', className)}
            href={links.financialIncentive}
            rel="noopener noreferrer"
            hidden>
            Notice of Financial Incentive
        </a>
    );
}

/**
 * "Your Privacy Choices" opt-out link (with the CCPA opt-out icon) — place in the footer near the
 * Privacy Policy link. Rendered `hidden`; Polaris reveals it only in states where opt-out applies.
 * The link text is fixed by TrueVault ("Your Privacy Choices") — do not change it.
 */
export function PrivacyChoicesOptOutLink({ className }: { className?: string }): ReactElement | null {
    const links = useTrueVaultPrivacyLinks();
    if (!links) return null;
    return (
        <a className={cn('truevault-polaris-optout', className)} href={links.optOut} rel="noopener noreferrer" hidden>
            <img
                src={links.optOutIconUrl}
                alt="California Consumer Privacy Act (CCPA) Opt-Out Icon"
                className="inline-block h-3.5 w-auto align-middle"
            />{' '}
            Your Privacy Choices
        </a>
    );
}
